import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UpdateIcon from '@mui/icons-material/Update';
import AddIcon from '@mui/icons-material/Add';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { makeStyles } from 'tss-react/mui';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { useSessionState } from '../../context/session/session';
import NotificationConfigDialog from './NotificationConfigDialog';

const CREATE_SAVED_FILTER = gql`
  mutation CreateSavedFilter($input: SavedFilterInput!, $anonymousToken: String) {
    createSavedFilter(input: $input, anonymousToken: $anonymousToken) {
      id
      name
      filterData
      filterType
      isDefault
      createdAt
    }
  }
`;

const UPDATE_SAVED_FILTER = gql`
  mutation UpdateSavedFilter($id: Int!, $input: SavedFilterInput!) {
    updateSavedFilter(id: $id, input: $input) {
      id
      name
      filterData
      filterType
      isDefault
      createdAt
    }
  }
`;

const useStyles = makeStyles()(() => ({
  saveButton: {
    margin: '5px 10px',
    width: '90%',
    alignSelf: 'center',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
}));

export function getAnonymousToken(): string | null {
  if (typeof window === 'undefined') return null;
  let token = localStorage.getItem('ouaaa_anonymous_token');
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem('ouaaa_anonymous_token', token);
  }
  return token;
}

interface SavedFilter {
  id: string | number;
  name: string;
  filterData?: any;
  filterType?: string;
}

interface SaveFilterButtonProps {
  currentFilters: any;
  filterType?: string;
  onFilterSaved?: (filter: SavedFilter) => void;
  forceOpen?: boolean;
  onDialogClose?: () => void;
  activeFilter?: SavedFilter | null;
}

function SaveFilterButton({ currentFilters, filterType, onFilterSaved, forceOpen, onDialogClose, activeFilter }: SaveFilterButtonProps) {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'update'>('create');
  const [filterName, setFilterName] = useState('');
  const [selectedFilterType, setSelectedFilterType] = useState(filterType || 'both');
  const [notifDialogOpen, setNotifDialogOpen] = useState(false);
  const [savedFilterForNotif, setSavedFilterForNotif] = useState<SavedFilter | null>(null);
  const user = useSessionState();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  const handleClose = () => {
    setOpen(false);
    setMode('create');
    if (onDialogClose) onDialogClose();
  };

  const handleOpenCreate = () => {
    setMode('create');
    setFilterName('');
    setSelectedFilterType(filterType || 'both');
    setOpen(true);
  };

  const handleOpenUpdate = () => {
    setMode('update');
    setFilterName(activeFilter?.name || '');
    setSelectedFilterType(activeFilter?.filterType || filterType || 'both');
    setOpen(true);
  };

  const [createSavedFilter, { loading: loadingCreate }] = useMutation(CREATE_SAVED_FILTER);
  const [updateSavedFilter, { loading: loadingUpdate }] = useMutation(UPDATE_SAVED_FILTER);

  const handleSave = async () => {
    if (!filterName.trim()) {
      enqueueSnackbar('Veuillez donner un nom à votre filtre', { variant: 'warning' });
      return;
    }

    try {
      if (mode === 'update' && activeFilter?.id) {
        const { data } = await updateSavedFilter({
          variables: {
            id: parseInt(String(activeFilter.id)),
            input: {
              name: filterName.trim(),
              filterData: currentFilters,
              filterType: selectedFilterType,
            },
          },
        });

        enqueueSnackbar(`Filtre "${filterName.trim()}" mis à jour !`, { variant: 'success' });
        handleClose();
        setFilterName('');
        if (onFilterSaved) onFilterSaved(data?.updateSavedFilter);
        if (data?.updateSavedFilter) {
          setSavedFilterForNotif(data.updateSavedFilter);
          setNotifDialogOpen(true);
        }
      } else {
        const anonymousToken = user ? null : getAnonymousToken();

        const { data } = await createSavedFilter({
          variables: {
            input: {
              name: filterName.trim(),
              filterData: currentFilters,
              filterType: selectedFilterType,
            },
            anonymousToken,
          },
        });

        if (!user && data?.createSavedFilter) {
          const localFilters = JSON.parse(localStorage.getItem('ouaaa_saved_filters') || '[]');
          localFilters.push({
            id: data.createSavedFilter.id,
            name: filterName.trim(),
            filterData: currentFilters,
            filterType: selectedFilterType,
            createdAt: new Date().toISOString(),
          });
          localStorage.setItem('ouaaa_saved_filters', JSON.stringify(localFilters));
        }

        enqueueSnackbar('Filtre sauvegardé !', { variant: 'success' });
        handleClose();
        setFilterName('');
        if (onFilterSaved) onFilterSaved(data?.createSavedFilter);
        if (data?.createSavedFilter) {
          setSavedFilterForNotif(data.createSavedFilter);
          setNotifDialogOpen(true);
        }
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erreur lors de la sauvegarde du filtre', { variant: 'error' });
    }
  };

  const loading = loadingCreate || loadingUpdate;

  return (
    <>
      <Box className={classes.buttonGroup}>
        {activeFilter ? (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={<UpdateIcon />}
              className={classes.saveButton}
              onClick={handleOpenUpdate}
            >
              Mettre à jour « {activeFilter.name} »
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              className={classes.saveButton}
              onClick={handleOpenCreate}
            >
              Créer un nouveau filtre
            </Button>
          </>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SaveIcon />}
            className={classes.saveButton}
            onClick={handleOpenCreate}
          >
            Sauvegarder ce filtre
          </Button>
        )}
      </Box>

      {notifDialogOpen && savedFilterForNotif && (
        <NotificationConfigDialog
          open={notifDialogOpen}
          filter={savedFilterForNotif}
          onClose={() => { setNotifDialogOpen(false); setSavedFilterForNotif(null); }}
        />
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {mode === 'update' ? 'Mettre à jour le filtre' : 'Sauvegarder le filtre'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du filtre"
            fullWidth
            variant="outlined"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            inputProps={{ maxLength: 100 }}
          />
          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel>Type de filtre</InputLabel>
            <Select
              value={selectedFilterType}
              onChange={(e) => setSelectedFilterType(e.target.value as string)}
              label="Type de filtre"
            >
              <MenuItem value="both">Acteurs et événements</MenuItem>
              <MenuItem value="actors">Acteurs uniquement</MenuItem>
              <MenuItem value="events">Événements uniquement</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={loading} endIcon={user ? <NotificationsIcon /> : undefined}>
            {mode === 'update' ? 'Mettre à jour' : 'Sauvegarder'}{user ? ' & notifs' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SaveFilterButton;
